import React from 'react';
import styled from 'styled-components';

const PlansSection = styled.section`
  padding: 100px 0;
  background: #f8faff;

  h2 {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 20px;
    color: #333;
  }

  .section-subtitle {
    text-align: center;
    color: #666;
    font-size: 1.1rem;
    max-width: 760px;
    margin: 0 auto 18px;
  }

  .section-microcopy {
    text-align: center;
    color: #667085;
    font-size: 0.95rem;
    margin-bottom: 46px;
  }

  .plan-group {
    margin-top: 52px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  .plan-group-title {
    text-align: center;
    color: #333;
    font-size: 1.55rem;
    margin: 0 0 24px;
  }

  .plans-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .plan-card {
    background: white;
    border-radius: 20px;
    padding: 40px 30px;
    width: 100%;
    max-width: 350px;
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.1);
    transition: all 0.3s ease;
    position: relative;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 20px 40px rgba(102, 126, 234, 0.2);
      border-color: #667eea;
    }

    &.featured {
      border-color: #667eea;
      background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);

      &::before {
        content: 'MAIS POPULAR';
        position: absolute;
        top: -12px;
        left: 50%;
        transform: translateX(-50%);
        background: #667eea;
        color: white;
        padding: 6px 20px;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
      }
    }

    .plan-header {
      text-align: center;
      margin-bottom: 30px;

      h4 {
        font-size: 1.8rem;
        color: #333;
        margin-bottom: 10px;
      }

      .price {
        font-size: 2.5rem;
        font-weight: 700;
        color: #667eea;
        margin-bottom: 5px;

        .period {
          font-size: 1rem;
          color: #666;
          font-weight: normal;
        }
      }

      .plan-description {
        color: #666;
        font-size: 0.95rem;
        line-height: 1.5;
      }

      .launch-badge {
        display: inline-block;
        margin: 0 0 12px;
        padding: 4px 10px;
        border-radius: 999px;
        background: #edf0ff;
        color: #5968d9;
        font-size: 0.78rem;
        font-weight: 600;
      }
    }

    .features-list {
      list-style: none;
      padding: 0;
      margin: 30px 0;

      li {
        padding: 12px 0;
        border-bottom: 1px solid #eee;
        color: #555;
        display: flex;
        align-items: center;
        gap: 10px;

        &:last-child {
          border-bottom: none;
        }

        .check-icon {
          color: #4caf50;
          font-weight: bold;
        }

        .limit {
          display: inline-block;
          background: #e3f2fd;
          color: #1976d2;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.8rem;
          margin-left: auto;
        }
      }
    }

    .plan-button {
      width: 100%;
      padding: 15px;
      border: none;
      border-radius: 50px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      text-align: center;
      display: block;
      text-decoration: none;

      &.primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
        }
      }

      &.secondary {
        background: transparent;
        color: #667eea;
        border: 2px solid #667eea;

        &:hover {
          background: #667eea;
          color: white;
        }
      }

      &.external-link {
        background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        color: white;

        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3);
          background: linear-gradient(135deg, #45a049 0%, #4caf50 100%);
        }
      }
    }

    .plan-note {
      font-size: 0.85rem;
      color: #888;
      text-align: center;
      margin-top: 15px;
      font-style: italic;
    }
  }

  .clinic-extra {
    text-align: center;
    margin-top: 50px;
    padding: 30px;
    background: #f0f4ff;
    border-radius: 15px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;

    p {
      color: #666;
      max-width: 680px;
      margin: 0 auto 12px;
      line-height: 1.6;
    }

    p:last-child {
      margin-bottom: 0;
    }

    strong {
      color: #333;
    }
  }

  .pricing-observation {
    max-width: 760px;
    margin: 28px auto 0;
    color: #667085;
    font-size: 0.88rem;
    line-height: 1.5;
    text-align: center;
  }
`;

const Plans = () => {
  const clinicContactLink =
    'mailto:suporte@codertec.com.br?subject=PsicoSense%20-%20Contato%20para%20cl%C3%ADnica&body=Ol%C3%A1%2C%20gostaria%20de%20falar%20sobre%20o%20PsicoSense%20para%20minha%20cl%C3%ADnica%20ou%20consult%C3%B3rio.';

  const professionalPlans = [
    {
      name: 'Plano Gratuito',
      price: 'R$ 0',
      period: '/mês',
      description:
        'Para conhecer o PsicoSense e testar os recursos principais.',
      features: [
        { text: 'Acesso inicial às áreas de apoio com IA', checked: true },
        { text: 'Limite reduzido de gerações por mês', checked: true },
        { text: 'Modelos básicos de documentos', checked: true },
        { text: 'Materiais de exemplo', checked: true },
        { text: 'Teste da plataforma sem compromisso', checked: true }
      ],
      buttonText: 'Começar grátis',
      buttonLink: 'https://app.psicosense.com.br/accounts/login/',
      buttonType: 'external-link',
      featured: false,
      note: 'Ideal para conhecer a plataforma antes de assinar.'
    },
    {
      name: 'Estudante',
      price: 'R$ 35',
      period: '/mês',
      description:
        'Para estudantes de Psicologia organizarem estudos, estágio, supervisões e materiais com apoio de IA.',
      features: [
        { text: '4 áreas de apoio com IA para estudo e estágio', checked: true },
        { text: 'Até 50 gerações por mês', checked: true, limit: '50' },
        { text: 'Exemplos de casos clínicos fictícios para estudo', checked: true },
        { text: 'Materiais educativos e fichas para imprimir', checked: true },
        { text: 'Apoio para atividades terapêuticas', checked: true },
        { text: 'Caderno de supervisão e anotações privadas, quando liberado', checked: true },
        { text: 'Suporte básico', checked: true }
      ],
      buttonText: 'Assinar Plano Estudante',
      buttonLink: 'https://app.psicosense.com.br/accounts/signup/?plano=estudante',
      buttonType: 'secondary',
      featured: false,
      note: 'Voltado para estudo, estágio e organização acadêmica. Não substitui supervisão profissional.'
    },
    {
      name: 'Psicólogo Individual',
      price: 'R$ 59',
      period: '/mês',
      badge: 'Preço de lançamento',
      description:
        'Para psicólogos autônomos que querem organizar agenda, pacientes, documentos e rotina clínica em um só lugar.',
      features: [
        { text: 'Todas as 4 áreas de apoio com IA', checked: true },
        { text: 'Documentos salvos ilimitados', checked: true },
        { text: 'Agenda e organização de sessões', checked: true },
        { text: 'Google Agenda e Google Meet', checked: true },
        { text: 'Prontuário/evolução', checked: true },
        { text: 'Financeiro básico', checked: true },
        { text: 'Exportação em PDF', checked: true },
        { text: 'Backup automático', checked: true },
        { text: 'Atualizações gratuitas', checked: true },
        { text: 'Suporte padrão', checked: true }
      ],
      buttonText: 'Assinar Plano Individual',
      buttonLink: 'https://app.psicosense.com.br/accounts/signup/?plano=profissional',
      buttonType: 'primary',
      featured: true,
      note: 'Valor especial para os primeiros assinantes. Novos planos poderão ser criados conforme novos recursos forem liberados.'
    }
  ];

  const clinicPlans = [
    {
      name: 'Clínica Essencial',
      price: 'R$ 119',
      period: '/mês',
      badge: 'Até 2 profissionais',
      description:
        'Para consultórios compartilhados, pequenos grupos e clínicas com dois profissionais que querem centralizar a gestão sem misturar dados clínicos.',
      features: [
        { text: 'Até 2 profissionais', checked: true },
        { text: 'Acesso individual para cada profissional', checked: true },
        { text: 'Agenda por profissional', checked: true },
        { text: 'Google Agenda e Google Meet por profissional', checked: true },
        { text: 'Pacientes separados por profissional', checked: true },
        { text: 'Documentos e prontuário por profissional', checked: true },
        { text: 'Dados clínicos isolados', checked: true },
        { text: 'Administração da assinatura pela clínica', checked: true },
        { text: 'Backup automático', checked: true },
        { text: 'Suporte padrão', checked: true }
      ],
      buttonText: 'Quero plano para clínica',
      buttonLink: clinicContactLink,
      buttonType: 'secondary',
      featured: false,
      note: 'A clínica paga a assinatura, mas cada profissional mantém sua área clínica individual.'
    },
    {
      name: 'Clínica Gestão',
      price: 'R$ 199',
      period: '/mês',
      badge: 'Até 5 profissionais',
      description:
        'Para clínicas com sócios, gestor, secretária ou equipe de profissionais.',
      features: [
        { text: 'Até 5 profissionais', checked: true },
        { text: 'Perfil de gestor/admin', checked: true },
        { text: 'Perfil de profissional', checked: true },
        { text: 'Perfil de secretária, quando liberado', checked: true },
        { text: 'Agenda geral da clínica', checked: true },
        { text: 'Agenda individual por profissional', checked: true },
        { text: 'Controle administrativo de pacientes', checked: true },
        { text: 'Financeiro administrativo', checked: true },
        { text: 'Relatórios da clínica', checked: true },
        { text: 'Google Agenda e Google Meet por profissional', checked: true },
        { text: 'Dados clínicos protegidos por permissão', checked: true },
        { text: 'Suporte prioritário', checked: true }
      ],
      buttonText: 'Falar com a equipe',
      buttonLink: clinicContactLink,
      buttonType: 'primary',
      featured: false,
      note: 'Gestão administrativa da clínica sem acesso automático ao prontuário/evolução clínica dos profissionais.'
    }
  ];

  const handlePlanClick = (plan) => {
    if (plan.buttonLink.startsWith('mailto:')) {
      window.location.href = plan.buttonLink;
      return;
    }

    window.open(plan.buttonLink, '_blank', 'noopener,noreferrer');
  };

  const renderPlanCard = (plan) => (
    <div key={plan.name} className={`plan-card ${plan.featured ? 'featured' : ''}`}>
      <div className="plan-header">
        <h4>{plan.name}</h4>
        {plan.badge && <span className="launch-badge">{plan.badge}</span>}
        <div className="price">
          {plan.price}
          <span className="period">{plan.period}</span>
        </div>
        <p className="plan-description">{plan.description}</p>
      </div>

      <ul className="features-list">
        {plan.features.map((feature, idx) => (
          <li key={idx}>
            <span className="check-icon">✓</span>
            {feature.text}
            {feature.limit && <span className="limit">{feature.limit}</span>}
          </li>
        ))}
      </ul>

      <button
        className={`plan-button ${plan.buttonType}`}
        onClick={() => handlePlanClick(plan)}
        aria-label={`${plan.buttonText} - ${plan.name}`}
      >
        {plan.buttonText}
      </button>

      {plan.note && <p className="plan-note">{plan.note}</p>}
    </div>
  );

  return (
    <PlansSection id="plans">
      <div className="container">
        <h2>Planos PsicoSense</h2>
        <p className="section-subtitle">
          Escolha o plano para conhecer a plataforma, organizar sua rotina individual ou
          centralizar a gestão administrativa da clínica com segurança para os dados clínicos.
        </p>
        <p className="section-microcopy">
          Comece sem precisar organizar tudo do zero e escolha a forma ideal de testar ou
          contratar o PsicoSense.
        </p>

        <div className="plan-group">
          <h3 className="plan-group-title">Para profissionais e estudantes</h3>
          <div className="plans-container">
            {professionalPlans.map(renderPlanCard)}
          </div>
        </div>

        <div className="plan-group">
          <h3 className="plan-group-title">Para clínicas e equipes</h3>
          <div className="plans-container">
            {clinicPlans.map(renderPlanCard)}
          </div>
        </div>

        <p className="pricing-observation">
          Valor especial para os primeiros assinantes. Novos planos poderão ser criados
          conforme novos recursos forem liberados.
        </p>

        <div className="clinic-extra">
          <p>
            <strong>Profissional adicional:</strong> R$ 39/mês por profissional extra.
          </p>
          <p>
            Permissões pensadas para a rotina da Psicologia: gestão administrativa da clínica
            não significa acesso automático ao prontuário ou às anotações clínicas dos
            profissionais.
          </p>
        </div>
      </div>
    </PlansSection>
  );
};

export default Plans;
